import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
	Collapse,
	List,
	CssBaseline,
	Divider,
	IconButton,
	ListItem,
	ListItemText,
	ListItemIcon,
	Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import { styled, Theme, CSSObject } from '@mui/material/styles';

import { useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate, useLocation } from 'react-router-dom';

import { PAGINATION_CONSTANTS, MODULES, ViewType } from 'common/constants';
import { DATAINGESTIONTABS } from 'common/constants/DataIngestionConstants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { REGULATION_TAB_VALUES } from 'common/constants/RegulationInventoryConstants';
import { ReportingLibraryTabs } from 'common/constants/ReportingLibraryConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import {
	ACTION_PLAN_TAB_CONSTANTS,
	TAXONOMY_ENTITY_INDEX,
} from 'common/constants/TaxonomyConstants';
import { setTMSelectedTab } from 'common/store/reducer/controlTestScriptReducer';
import { setRetainSearchText } from 'common/store/reducer/data-ingestion-reducer';
import {
	setClearAllDates,
	setHorizonAppliedFilterList,
	setHorizonApplyFilterEnabled,
	setHorizonFilter,
	setHorizonFilterFlag,
	setHorizonLandingSearchText,
	setHorizonSelectedAppliedFilterList,
	setSelectedDateArray,
	setSelectedDueDate,
	setSelectedEffectiveDate,
	setShowAssignedToMe,
} from 'common/store/reducer/horizonScanReducer';
import {
	setSelectedTabValue,
	setTaxonomyDetailsTabIndex,
} from 'common/store/reducer/mappingDetailsReducer';
import {
	setRegInventorySearchText,
	setRegulationToggleButton,
	setRegInventoryAppliedFilters,
	setRegInvLandingSelectedTab,
} from 'common/store/reducer/regulationInventoryReducer';
import {
	setModelTabIndex,
	setSimulationControlOwnerValue,
	setSimulationControlTypeValue,
	setSimulationControlFrequencyValue,
	setSimulationTabIndex,
	setSimulationViewTypeValue,
} from 'common/store/reducer/simulationReducer';
import {
	setRiskRefApplyFilterEnabled,
	setRiskRefSearchText,
	setRiskRefTaxonomyFilter,
	setRiskRefTaxonomySourceFiltersKey,
	setRiskRefTaxonomySourceFiltersKeyFlag,
	setTaxonomyViewTypeValue,
	setTaxonomySelectedAppliedFilterList,
	setEntityResponseData,
	setEntityCountData,
	setOriginalEntityResponseData,
	setAllEntityResponseData,
	setAllOriginalEntityResponseData,
	setIsPRCEditDataDeleting,
	setIsRegenerateMappingBtnClicked,
} from 'common/store/reducer/taxonomyReducer';
import {
	setUserManagementSelectedTab,
	setOverviewSelectedTab,
	setPermissionsTabIndex,
} from 'common/store/reducer/userManagementReducer';
import { isModuleExists } from 'common/utils/authCheck';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import {
	AdminSettingsIcon,
	metricStreamLogo,
	cubeLogo,
	msPilotLogo,
	signavioLogo,
	openSideBar,
	closeSideBar,
	riskProfileIcon,
	changeEngineIcon,
	orgChartIcon,
	reportingIcon,
	dataIngestionIcon,
	testingIcon,
	aiModelsIcon,
} from '../../../assets/images';
import { RootState } from '../../../store/AppStore';
import {
	clearTablePaginationHolder,
	setAppDrawer,
	setHomePageTabIndex,
	setPagination,
	setRegChangeSearchText,
	setRegChangeSelectedTabIndex,
	setRequirementInventoryBreadcrumbText,
	setRiskRefSelectedTab,
	setMultiSelectInstitutionFilterData,
	setMultiSelectJurisdictionFilterData,
	setMultiSelectProductFilterData,
	setHorizonScanningSearchText,
	setMappingDrawer,
	setIsRelationshipMappingDeleted,
	setSelectedReportingTab,
	clearUploadedDocumentProgress,
	setSelectedDataIngestionTab,
	setIsPRCDetailsPage,
} from '../../../store/reducer/commonReducer';
import {
	scrollProps,
	hasChildren,
	isModuleEnabled,
	toSentenceCase,
	urlWithTenant,
} from '../../../utils/utilFunctions';

import styles from './SideBar.module.scss';

const { IS_PRC_CARD_VIEW_ENABLED } = FeatureFlagsKeysEnum;
const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
	width: drawerWidth,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up('sm')]: {
		width: `calc(${theme.spacing(8)} + 1px)`,
	},
});

const DrawerHeader = styled('div')(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	padding: theme.spacing(0, 1),
	// necessary for content to be below app bar
	...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
	shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
	width: drawerWidth,
	flexShrink: 0,
	whiteSpace: 'nowrap',
	boxSizing: 'border-box',
	...(open && {
		...openedMixin(theme),
		'& .MuiDrawer-paper': openedMixin(theme),
	}),
	...(!open && {
		...closedMixin(theme),
		'& .MuiDrawer-paper': closedMixin(theme),
	}),
}));

const {
	prcInventory,
	horizonScanning,
	changeEngine,
	regulationChange,
	riskChange,
	regulationInventory,
	simulationArena,
	testing,
	reporting,
	dataIngestion,
	management,
} = rrcpDashboardRoutes;

const { RECENTLY_VIEWED_TAB } = ReportingLibraryTabs;
const { TABLE, GRID, CARD } = ViewType;
const { ALL } = DATAINGESTIONTABS;

const getSideBarList = (
	features: Record<string, any>,
	modulesList: Record<string, any>,
) => [
	{
		text: 'PRC inventory',
		label: 'risk-profile',
		path: urlWithTenant(`/${prcInventory}`),
		icon: riskProfileIcon,
		isEnabled: isModuleEnabled(
			features.isRiskReferenceTaxonomyEnabled,
			modulesList.isRiskReferenceTaxonomyModuleExists,
		),
	},

	{
		text: 'Horizon scanning',
		label: 'horizonscan',
		path: urlWithTenant(`/${horizonScanning}`),
		icon: changeEngineIcon,
		isEnabled: isModuleEnabled(
			features.isHorizonScanningEnabled,
			modulesList.isHorizonScanningModuleExists,
		),
	},

	{
		text: 'Change engine',
		label: 'changeengine',
		path: urlWithTenant(`/${changeEngine}`),
		icon: changeEngineIcon,
		isEnabled: isModuleEnabled(
			features.isChangeEngineEnabled,
			modulesList.isChangeEngineModuleExists,
		),
		items: [
			{
				text: 'Regulation change',
				label: 'regulationchange',
				path: `/${regulationChange}`,
				isEnabled: isModuleEnabled(
					features.isRegulationChangeEnabled,
					modulesList.isRegulationChangeModuleExists,
				),
			},
			{
				text: 'Risk change',
				label: 'riskchange',
				path: `/${riskChange}`,
				isEnabled: isModuleEnabled(
					features.isRiskChangeEnabled,
					modulesList.isRiskChangeModuleExists,
				),
			},
		],
	},
	{
		text: toSentenceCase('Requirements Inventory'),
		label: 'requirement-inventory',
		path: urlWithTenant(`/${regulationInventory}`),
		icon: orgChartIcon,
		isEnabled: isModuleEnabled(
			features.isRequirementInventoryEnabled,
			modulesList.isRequirementInventoryModuleExists,
		),
	},
	{
		text: 'Simulation arena',
		label: 'simulate-arena',
		path: urlWithTenant(`/${simulationArena}`),
		icon: aiModelsIcon,
		isEnabled: isModuleEnabled(
			features.isAiSimulationAndExperimentationEnabled,
			modulesList.isSimulationArenaModuleExists,
		), // this module will be enabled by default irrespective of feature flag onlyAiSimulation
	},
	{
		text: 'Testing & monitoring',
		label: 'testing',
		path: urlWithTenant(`/${testing}`),
		icon: testingIcon,
		isEnabled: isModuleEnabled(
			features.isTestingAndMonitoringEnabled,
			modulesList.isTestingAndMonitoringModuleExists,
		),
	},
	{
		text: 'Reporting library',
		label: 'reporting',
		path: urlWithTenant(`/${reporting}`),
		icon: reportingIcon,
		isEnabled: isModuleEnabled(
			features.isReportingLibraryEnabled,
			modulesList.isReportingLibraryModuleExists,
		),
	},
	{
		text: 'Data ingestion',
		label: 'dataingestion',
		path: urlWithTenant(`/${dataIngestion}`),
		icon: dataIngestionIcon,
		isEnabled: isModuleEnabled(
			features.isDataIngestionEnabled,
			modulesList.isDataIngestionModuleExists,
		),
	},
];

const {
	REACT_APP_METRIC_STREAM,
	REACT_APP_CUBE,
	REACT_APP_SIGNAVIO,
	REACT_APP_MS_COPILOT,
} = process.env;

const { TAB_MAPPING } = ACTION_PLAN_TAB_CONSTANTS;

const getSidebarMiscList = (
	features: Record<string, any>,
	modulesList: Record<string, any>,
) => [
	{
		text: 'Management',
		icon: AdminSettingsIcon,
		path: urlWithTenant(`/${management}`),
		isEnabled: isModuleEnabled(
			features.isManagementEnabled,
			modulesList.isManagementModuleEnabled,
		),
	},
];

const MenuItem = ({ item }: any) => {
	const Component = hasChildren(item) ? MultiLevel : SingleLevel;
	return item.isEnabled ? (
		<Component item={item} data-testid={item.text} />
	) : null;
};

const SingleLevel = ({ item }: any) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const isAppDrawerOpen = useSelector(
		(state: RootState) => state.common.isAppDrawerOpen,
	);

	const taxonomyDetailTabIndex: string = useSelector(
		(state: RootState) => state?.mappingDetails?.taxonomyDetailsTabIndex,
	);
	const isPrcCardViewEnabled = useFeatureFlag(IS_PRC_CARD_VIEW_ENABLED);
	const setIndexValues = (item: any) => {
		const { PROCESS } = TAXONOMY_ENTITY_INDEX;
		const { REACT_APP_ENVIRONMENT } = process.env;

		if (item.label === 'risk-profile') {
			dispatch(
				setRiskRefSelectedTab({
					tableIndex: `${PROCESS}-${REACT_APP_ENVIRONMENT}`,
					indexType: 0,
				}),
			);
			dispatch(setRiskRefTaxonomyFilter([]));
			dispatch(setRiskRefSearchText(''));
			dispatch(setMultiSelectProductFilterData([]));
			dispatch(setMultiSelectJurisdictionFilterData([]));
			dispatch(setMultiSelectInstitutionFilterData([]));
			dispatch(setRetainSearchText(''));
			dispatch(setHorizonLandingSearchText(''));
			dispatch(setHorizonAppliedFilterList([]));
			dispatch(setShowAssignedToMe(false));
			dispatch(setHorizonApplyFilterEnabled(false));
			dispatch(setHorizonFilterFlag(false));
			dispatch(setHorizonFilter([]));
			dispatch(setHorizonSelectedAppliedFilterList([]));
			dispatch(setRiskRefApplyFilterEnabled(false));
			dispatch(setSelectedDateArray([]));
			dispatch(setSelectedDueDate(''));
			dispatch(setSelectedEffectiveDate(''));
		}
		if (item.label === 'regulationchange') {
			dispatch(setRegChangeSelectedTabIndex(0));
			dispatch(setRegChangeSearchText(''));
			dispatch(setHorizonAppliedFilterList([]));
			dispatch(setShowAssignedToMe(false));
			dispatch(setHorizonApplyFilterEnabled(false));
			dispatch(setHorizonFilterFlag(false));
			dispatch(setHorizonFilter([]));
			dispatch(setHorizonSelectedAppliedFilterList([]));
			dispatch(setHorizonLandingSearchText(''));
			dispatch(setSelectedDateArray([]));
			dispatch(setSelectedDueDate(''));
			dispatch(setSelectedEffectiveDate(''));
		}
		if (item.label === 'requirement-inventory') {
			dispatch(
				setRegInvLandingSelectedTab(REGULATION_TAB_VALUES.CURRENT_INVENTORY),
			);
			dispatch(setRegInventorySearchText(''));
			dispatch(setRegulationToggleButton('regulations'));
			dispatch(setRegInventoryAppliedFilters([]));
			dispatch(setHorizonLandingSearchText(''));
			dispatch(setHorizonAppliedFilterList([]));
			dispatch(setShowAssignedToMe(false));
			dispatch(setHorizonApplyFilterEnabled(false));
			dispatch(setHorizonFilterFlag(false));
			dispatch(setHorizonFilter([]));
			dispatch(setHorizonSelectedAppliedFilterList([]));
			dispatch(setSelectedDateArray([]));
			dispatch(setSelectedDueDate(''));
			dispatch(setSelectedEffectiveDate(''));
			dispatch(setTaxonomyViewTypeValue(isPrcCardViewEnabled ? CARD : TABLE));
		}
		if (item.label === 'testing') {
			dispatch(setTMSelectedTab(0));
			dispatch(setHorizonLandingSearchText(''));
			dispatch(setHorizonAppliedFilterList([]));
			dispatch(setShowAssignedToMe(false));
			dispatch(setHorizonApplyFilterEnabled(false));
			dispatch(setHorizonSelectedAppliedFilterList([]));
			dispatch(setHorizonFilterFlag(false));
			dispatch(setHorizonFilter([]));
			dispatch(setSelectedDateArray([]));
			dispatch(setSelectedDueDate(''));
			dispatch(setSelectedEffectiveDate(''));
		}
		if (item.label === 'dataingestion') {
			dispatch(setRetainSearchText(''));
			dispatch(setHorizonLandingSearchText(''));
			dispatch(setHorizonAppliedFilterList([]));
			dispatch(setHorizonSelectedAppliedFilterList([]));
			dispatch(setShowAssignedToMe(false));
			dispatch(setHorizonApplyFilterEnabled(false));
			dispatch(setHorizonFilterFlag(false));
			dispatch(setHorizonFilter([]));
			dispatch(setSelectedDateArray([]));
			dispatch(setSelectedDueDate(''));
			dispatch(setSelectedEffectiveDate(''));
			dispatch(
				setSelectedDataIngestionTab({
					dataIngestionTabIndex: 0,
					dataIngestionTabName: ALL,
				}),
			);
		}
		if (item.label === 'reporting') {
			dispatch(
				setSelectedReportingTab({
					reportingLibraryTabIndex: 0,
					reportingLibraryTabName: RECENTLY_VIEWED_TAB,
				}),
			);
		}
	};

	const currentLocation = useMemo(() => location.pathname, [location]);

	const onClickMenuHandler = () => {
		if (currentLocation !== item.path) {
			dispatch(clearTablePaginationHolder());
		}
		navigate(item.path);
		setIndexValues(item);
		dispatch(
			setRequirementInventoryBreadcrumbText(MODULES.REQUIREMENT_INVENTORY),
		);
		dispatch(setTaxonomyDetailsTabIndex(taxonomyDetailTabIndex));
		dispatch(setRiskRefTaxonomySourceFiltersKey(''));
		dispatch(setRiskRefTaxonomySourceFiltersKeyFlag(false));
		dispatch(setRiskRefApplyFilterEnabled(false));
		dispatch(
			setPagination({
				sortKey: '',
				pageSize: PAGINATION_CONSTANTS.PAGE_SIZE,
				pageNumber: PAGINATION_CONSTANTS.PAGE_NUMBER,
				sortDirection: '',
			}),
		);
		dispatch(setUserManagementSelectedTab(0));
		dispatch(setOverviewSelectedTab(0));
		dispatch(setPermissionsTabIndex(0));
		dispatch(setHomePageTabIndex(0));
		dispatch(setRegChangeSearchText(''));
		dispatch(setRetainSearchText(''));
		dispatch(setSimulationTabIndex(0));
		dispatch(setSelectedTabValue(TAB_MAPPING));
		dispatch(setModelTabIndex(0));
		dispatch(setSimulationControlOwnerValue(''));
		dispatch(setSimulationControlTypeValue(''));
		dispatch(setSimulationControlFrequencyValue(''));
		dispatch(setSimulationViewTypeValue(GRID));
		dispatch(setHorizonScanningSearchText(''));
		dispatch(setMappingDrawer(false));
		dispatch(setTaxonomyViewTypeValue(isPrcCardViewEnabled ? CARD : TABLE));
		dispatch(setTaxonomySelectedAppliedFilterList([]));
		dispatch(setClearAllDates());
		dispatch(setIsRelationshipMappingDeleted(false));
		dispatch(clearUploadedDocumentProgress());
		dispatch(setEntityResponseData([]));
		dispatch(setEntityCountData([]));
		dispatch(setOriginalEntityResponseData([]));
		dispatch(setAllEntityResponseData([]));
		dispatch(setAllOriginalEntityResponseData([]));
		dispatch(setIsPRCDetailsPage(false));
		dispatch(setIsPRCEditDataDeleting(false));
		dispatch(setIsRegenerateMappingBtnClicked(false));
	};

	return (
		<ListItem
			onClick={onClickMenuHandler}
			className={
				location?.pathname === item.path
					? styles['selected-item']
					: styles['cursor-pointer']
			}
			data-testid={`menu-item-${item.label}`}
		>
			{item.icon && (
				<ListItemIcon>
					<img src={item.icon} alt={item.text} />
				</ListItemIcon>
			)}
			<ListItemText
				primary={item.text}
				className={
					styles[
						`${
							item.icon
								? 'sidebar-item-start-without-padding'
								: 'sidebar-item-start-with-padding'
						}`
					]
				}
				primaryTypographyProps={
					isAppDrawerOpen
						? {
								style: {
									whiteSpace: 'normal',
								},
						  }
						: {}
				}
			/>
		</ListItem>
	);
};

const MultiLevel = ({ item }: any) => {
	const { items: children } = item;
	const [open, setOpen] = useState(false);
	const dispatch = useDispatch();
	const taxonomyDetailTabIndex: string = useSelector(
		(state: RootState) => state?.mappingDetails?.taxonomyDetailsTabIndex,
	);
	const handleClick = () => {
		setOpen((prev) => !prev);
		dispatch(setTaxonomyDetailsTabIndex(taxonomyDetailTabIndex));
	};

	return (
		<>
			<ListItem button onClick={handleClick}>
				<ListItemIcon>
					<img src={item.icon} alt={item.text} />
				</ListItemIcon>
				<ListItemText primary={item.text} />
				{open ? <ExpandLess /> : <ExpandMore />}
			</ListItem>
			<Collapse in={open} timeout="auto" unmountOnExit>
				<List component="div" disablePadding>
					{children.map((child: any) => (
						<MenuItem key={child} item={child} />
					))}
				</List>
			</Collapse>
		</>
	);
};

const SideBar = () => {
	const isAppDrawerOpen = useSelector(
		(state: RootState) => state.common.isAppDrawerOpen,
	);

	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);

	const dispatch = useDispatch();
	const externalApplications = [
		{
			text: 'Metric Stream',
			icon: metricStreamLogo,
			url: REACT_APP_METRIC_STREAM,
			enabled: useFeatureFlag(
				FeatureFlagsKeysEnum.IS_METRIC_STREAM_ENABLED,
				isLDResponseExists,
			),
		},
		{
			text: 'CUBE',
			icon: cubeLogo,
			url: REACT_APP_CUBE,
			enabled: useFeatureFlag(
				FeatureFlagsKeysEnum.IS_CUBE_ENABLED,
				isLDResponseExists,
			),
		},
		{
			text: 'Microsoft CoPilot',
			icon: msPilotLogo,
			url: REACT_APP_MS_COPILOT,
			enabled: useFeatureFlag(
				FeatureFlagsKeysEnum.IS_MICROSOFT_COPILOT_ENABLED,
				isLDResponseExists,
			),
		},
		{
			text: 'SIGNAVIO',
			icon: signavioLogo,
			url: REACT_APP_SIGNAVIO,
			enabled: useFeatureFlag(
				FeatureFlagsKeysEnum.IS_SIGNAVIO_ENABLED,
				isLDResponseExists,
			),
		},
	];

	// Feature flags with fallbacks enabled.
	const sidebarMiscListFeatureFlags = {
		isManagementEnabled: useFeatureFlag(
			FeatureFlagsKeysEnum.IS_MANAGEMENT_ENABLED,
			isLDResponseExists,
		),
	};
	const sidebarMiscListModulesList = {
		isManagementModuleEnabled: isModuleExists(MODULES.MANAGEMENT),
	};
	const isExternalAppsEnabled = () => {
		const isAppsEnabled = externalApplications.some(
			(ele: any) => ele?.enabled === true,
		);
		return isAppsEnabled;
	};

	const aiSimulationEnabled =
		FeatureFlagsKeysEnum.IS_AI_SIMULATION_AND_EXPERIMENTATION_ENABLED;

	const sideBarListFeatureFlags = {
		isRiskReferenceTaxonomyEnabled: useFeatureFlag(
			FeatureFlagsKeysEnum.IS_RISK_REFERENCE_TAXONOMY_ENABLED,
			isLDResponseExists,
		),
		isChangeEngineEnabled: useFeatureFlag(
			FeatureFlagsKeysEnum.IS_CHANGE_ENGINE_ENABLED,
			isLDResponseExists,
		),
		isRegulationChangeEnabled: useFeatureFlag(
			FeatureFlagsKeysEnum.IS_REGULATION_CHANGE_ENABLED,
			isLDResponseExists,
		),
		isRiskChangeEnabled: useFeatureFlag(
			FeatureFlagsKeysEnum.IS_RISK_GPT_ENABLED,
			isLDResponseExists,
		),
		isRequirementInventoryEnabled: useFeatureFlag(
			FeatureFlagsKeysEnum.IS_REGULATION_INVENTORY_ENABLED,
			isLDResponseExists,
		),
		isAiSimulationAndExperimentationEnabled: useFeatureFlag(
			aiSimulationEnabled,
			isLDResponseExists,
		),
		isTestingAndMonitoringEnabled: useFeatureFlag(
			FeatureFlagsKeysEnum.IS_TESTING_AND_MONITORING_ENABLED,
			isLDResponseExists,
		),
		isReportingLibraryEnabled: useFeatureFlag(
			FeatureFlagsKeysEnum.IS_REPORTING_LIBRARY_ENABLED,
			isLDResponseExists,
		),
		isDataIngestionEnabled: useFeatureFlag(
			FeatureFlagsKeysEnum.IS_DATA_INGESTION_ENABLED,
			isLDResponseExists,
		),
		isHorizonScanningEnabled: useFeatureFlag(
			FeatureFlagsKeysEnum.IS_HORIZON_SCANNING_ENABLED,
			isLDResponseExists,
		),
	};

	const sideBarModulesList: any = {
		isRequirementInventoryModuleExists: isModuleExists(
			MODULES.REQUIREMENT_INVENTORY,
		),
		isChangeEngineModuleExists: isModuleExists([
			MODULES.CHANGE_ENGINE,
			MODULES.REGULATION_CHANGE,
		]),
		isTestingAndMonitoringModuleExists: isModuleExists(
			MODULES.TESTING_AND_MONITORING,
		),
		isRiskReferenceTaxonomyModuleExists: isModuleExists(
			MODULES.RISK_REFERENCE_TAXONOMY,
		),
		isReportingLibraryModuleExists: isModuleExists(MODULES.REPORTING_DATABASE),
		isSimulationArenaModuleExists: isModuleExists(
			MODULES.AI_SIMULATION_AND_EXPERIMENTATION,
		),
		isDataIngestionModuleExists: isModuleExists(MODULES.DATA_INGESTION),
		isRegulationChangeModuleExists: isModuleExists(MODULES.REGULATION_CHANGE),
		isRiskChangeModuleExists: isModuleExists(MODULES.RISK_CHANGE),
		isMappingEngineModuleExists: isModuleExists(MODULES.MAPPING_ENGINE),
		isGrcModuleExists: isModuleExists(MODULES.GRC),
		isModifiedChangesModuleExists: isModuleExists(MODULES.MODIFIED_CHANGES),
		isRiskQuantificationModuleExists: isModuleExists(
			MODULES.RISK_QUANTIFICATION,
		),
		isHorizonScanningModuleExists: isModuleExists(
			MODULES.HORIZON_SCAN_AND_ALERTS,
		),
	};
	const currentSideBar = getSideBarList(
		sideBarListFeatureFlags,
		sideBarModulesList,
	);

	const handleExternalAppClick = (url: string) => {
		if (url) {
			window.open(url, '_blank');
		}
	};

	return (
		<Box className={styles['container-elem']} data-testid="box-element-display">
			<CssBaseline />{' '}
			<Drawer variant="permanent" open={isAppDrawerOpen}>
				<DrawerHeader />

				{/* Main apps */}
				<List className={styles['scroll-style']} sx={scrollProps()}>
					{currentSideBar?.map((item) => (
						<MenuItem key={item.text} item={item} />
					))}
				</List>

				<Divider />

				{/* External Apps */}
				<List component="div">
					{externalApplications.map(
						(item: any) =>
							item.enabled && (
								<div
									key={item.text}
									role="presentation"
									onClick={() => handleExternalAppClick(item.url ?? '')}
									className={styles['external-app-div']}
									data-testid="external-app"
								>
									<div>
										<img
											src={item.icon}
											alt="external-app-icon"
											className={styles['external-app-icon']}
										/>
									</div>
									<Typography
										className={`${styles['external-app']} ${
											isAppDrawerOpen ? '' : styles['open-class']
										}`}
									>
										{item.text}
									</Typography>
								</div>
							),
					)}
				</List>

				{isExternalAppsEnabled() && <Divider />}

				{/* Misc List */}
				<div className={styles['misc-container']}>
					{getSidebarMiscList(
						sidebarMiscListFeatureFlags,
						sidebarMiscListModulesList,
					).map((item) => (
						<MenuItem key={item.text} item={item} />
					))}
				</div>
				<DrawerHeader className={styles['collapse-div']}>
					<IconButton
						onClick={() => {
							dispatch(setAppDrawer(!isAppDrawerOpen));
						}}
						disableRipple
						className={styles['cursor-pointer']}
					>
						{isAppDrawerOpen ? (
							<img src={closeSideBar} alt="collapse" />
						) : (
							<img src={openSideBar} alt="collapse" />
						)}
						{isAppDrawerOpen && (
							<p className={styles['collapse-style']}>Collapse</p>
						)}
					</IconButton>
				</DrawerHeader>
			</Drawer>
		</Box>
	);
};

export default SideBar;
